import {
  GameHandler,
  CardModalData,
  DeckBuilderCardFunctions,
  CardOrientation,
} from "@/interfaces/GameHandler";
import { CardItem } from "@/interfaces/CardItem";
import { DeckIdentity } from "@/interfaces/DeckIdentity";
import { SrcType } from "@/models/alt-arts";
import { DeckCardsMap } from "@/interfaces/DeckIdentity";
import { getTCGPlayerCardImage } from "@/lib/tcgplayer/tcgplayer_helper";
import { CARDS_PER_PAGE } from "@/components/DeckBuilder/config";
import { getImgproxyUrl } from "@/lib/imgproxy";

/**
 * Configuration interface for game-specific settings
 */
export interface GameConfig {
  // CDN and image settings
  cdnBaseUrl: string;
  setImagePath: string;

  // Game mechanics
  hasLeader: boolean;

  // Card data functions
  cardDataFunctions: {
    getCardData: (cardId: string) => CardItem | undefined;
    getCardImage: (cardId: string, size?: string, src?: SrcType | "") => string;
    getAltArts: (cardId: string, showAAs: boolean) => any[];
    getAACardId: (aaItem: any) => string;
    getAACardIdData?: (aaCardId: string) => CardItem | undefined;
    getAAFromCardId: (aaCardId: string) => any;
    cleanCardData: () => Record<string, CardItem>;
  };

  // Leader functions
  getLeaderData?: (leaderId: string) => DeckIdentity | undefined;

  // TCGPlayer integration
  getTcgPlayerCardData?: (tcgplayerId: string) => CardItem | undefined;

  // Filters and sorting
  getDefaultFilters: () => any;
  filterCards: (
    cards: CardItem[],
    filters: any,
    defaultFilters: any,
    deckCardsMap: DeckCardsMap,
  ) => CardItem[];
  sortCards: (a: CardItem, b: CardItem) => number;
  sortDecklist: (cardList: string, leader?: string) => string;

  // Card modal configuration
  getCardType: (card: CardItem) => string;
  getCardEffect: (card: CardItem) => React.ReactNode[];
  getCardTrigger: (card: CardItem) => React.ReactNode[];
  getCardLink?: (card: CardItem, tcgplayerId?: string | null) => string;
  getAdditionalChips?: (card: CardItem) => string[];

  /**
   * How a card's stored art is shaped, for the games that print some cards
   * sideways. Omitted means every card is portrait.
   */
  getCardOrientation?: (card: CardItem) => CardOrientation;

  // Optional game-specific field accessors
  getCost?: (card: CardItem) => number;
  getEnergyCost?: (card: CardItem) => number;
  getPowerCost?: (card: CardItem) => number;
  getColor?: (card: CardItem) => string;
  getPower?: (card: CardItem) => number;
  isLeader?: (card: CardItem) => boolean;
}

/**
 * Base handler class that implements common GameHandler functionality
 * Extend this class and provide a GameConfig to create a new game handler
 */
export abstract class BaseHandler implements GameHandler {
  protected config: GameConfig;

  constructor(config: GameConfig) {
    this.config = config;
  }

  // Core methods
  getSetImage(set: string): string {
    // Use CDN directly without imgproxy for set squares
    return `${this.config.cdnBaseUrl}${this.config.setImagePath}${set}.webp?v=1.0.1`;
  }

  getCardImage(cardId: string, size: string, src: SrcType | "" = ""): string {
    return this.config.cardDataFunctions.getCardImage(cardId, size, src);
  }

  getTcgPlayerCardData(tcgplayerId: string): CardItem | undefined {
    if (this.config.getTcgPlayerCardData) {
      return this.config.getTcgPlayerCardData(tcgplayerId);
    }
    return undefined;
  }

  getCardData(cardId: string): CardItem | undefined {
    return this.config.cardDataFunctions.getCardData(cardId);
  }

  /**
   * Set/expansion code. Games whose raw data names this differently override
   * this; the default reads a plain `set` field when one exists.
   */
  getSet(card: CardItem): string {
    return (card as { set?: string }).set ?? "";
  }

  getLeaderData(leaderId: string): DeckIdentity | undefined {
    if (this.config.getLeaderData) {
      return this.config.getLeaderData(leaderId);
    }
    // Default fallback for games without custom leader logic
    const cardData = this.getCardData(leaderId);
    if (!cardData) return undefined;

    return {
      id: leaderId,
      name: cardData.name || "",
      color: cardData.color || "",
      set: this.getSet(cardData),
      // Don't set image here - let LeaderSquareWithBorder use getLeaderSquare fallback
    };
  }

  hasLeader(): boolean {
    return this.config.hasLeader;
  }

  sortDecklist(cardList: string, leader?: string): string {
    return this.config.sortDecklist(cardList, leader);
  }

  getCardModalData(): CardModalData {
    return {
      getCardImage: (cardId: string, tcgplayerId?: string | null) => {
        if (tcgplayerId) {
          return getTCGPlayerCardImage(tcgplayerId);
        }
        return this.config.cardDataFunctions.getCardImage(cardId);
      },
      getCardType: this.config.getCardType,
      getCardEffect: this.config.getCardEffect,
      getCardTrigger: this.config.getCardTrigger,
      getCardLink:
        this.config.getCardLink ||
        ((card, tcgplayerId) => {
          return tcgplayerId
            ? `https://www.tcgplayer.com/product/${tcgplayerId}`
            : "#";
        }),
      getAdditionalChips: this.config.getAdditionalChips,
    };
  }

  // DeckBuilder-specific methods
  getDeckBuilderCardFunctions(): DeckBuilderCardFunctions {
    return {
      getCardData: this.config.cardDataFunctions.getCardData,
      getAltArts: this.config.cardDataFunctions.getAltArts,
      getAACardId: this.config.cardDataFunctions.getAACardId,
      getAACardIdData: this.config.cardDataFunctions.getAACardIdData,
      getAAFromCardId: this.config.cardDataFunctions.getAAFromCardId,
      cleanCardData: this.config.cardDataFunctions.cleanCardData,
    };
  }

  getDefaultFilters() {
    return this.config.getDefaultFilters();
  }

  /**
   * Portrait unless the game says otherwise, so a game that never prints a card
   * sideways does not have to mention orientation at all.
   */
  getCardOrientation(card: CardItem): CardOrientation {
    return this.config.getCardOrientation?.(card) ?? "portrait";
  }

  getCardsPerPage(): number {
    return CARDS_PER_PAGE;
  }

  getModalCardData(aaCardId: string): CardItem | undefined {
    if (!aaCardId) return undefined;

    // If game has custom AA card data function, use it
    if (this.config.cardDataFunctions.getAACardIdData) {
      return this.config.cardDataFunctions.getAACardIdData(aaCardId);
    }

    // Otherwise use standard card data
    return this.config.cardDataFunctions.getCardData(aaCardId);
  }

  filterCards(
    cards: CardItem[],
    filters: any,
    defaultFilters: any,
    deckCardsMap: DeckCardsMap,
  ): CardItem[] {
    return this.config.filterCards(
      cards,
      filters,
      defaultFilters,
      deckCardsMap,
    );
  }

  sortCards(a: CardItem, b: CardItem): number {
    return this.config.sortCards(a, b);
  }

  // Optional game-specific methods
  getCost?(card: CardItem): number {
    return this.config.getCost?.(card) ?? 0;
  }

  getEnergyCost?(card: CardItem): number {
    return this.config.getEnergyCost?.(card) ?? 0;
  }

  getPowerCost?(card: CardItem): number {
    return this.config.getPowerCost?.(card) ?? 0;
  }

  getColor?(card: CardItem): string {
    return this.config.getColor?.(card) ?? "";
  }

  getPower?(card: CardItem): number {
    return this.config.getPower?.(card) ?? 0;
  }

  isLeader?(card: CardItem): boolean {
    return this.config.isLeader?.(card) ?? false;
  }
}
